import { useSnackbar } from './../../stores/snackbar';
import { AccountInfo, AuthenticationResult, RedirectRequest } from '@azure/msal-browser';
import { useUser } from '~~/stores/user';
import { User } from '../resources/User';
import { Http } from '../Http';
import { useLogs } from '~~/composables/useLogs';
import { NetworkError } from '~~/data/models/NetworkError';
import { AxiosError } from 'axios';

export class AuthService {
  // #region instance
  private static Instance: AuthService;
  private user = useUser();
  private cachedToken: { value: string; expMs: number } | null = null;

  protected constructor() {
    //
  }

  public static get instance(): AuthService {
    if (!this.Instance) {
      this.Instance = new AuthService();
    }
    return this.Instance;
  }
  // #endregion

  // #endregion
  private loginRequest: RedirectRequest = (() => {
    const config = useConfiguration();
    const extraQueryParameters: Record<string, string> = {};

    if (config.USE_SSO_IDP) {
      extraQueryParameters.acr_values = `idp:${config.USE_SSO_IDP}`;
    }

    return {
      scopes: config.LOGIN_SCOPES,
      extraQueryParameters,
      loginHint: '',
    };
  })();

  public async getAccessToken(): Promise<string> {
    try {
      const skew = 30_000;
      if (this.cachedToken && Date.now() + skew < this.cachedToken.expMs) {
        return this.cachedToken.value;
      }
      const result: AuthenticationResult = await this.tryAcquireTokenSilent();

      if (!result?.accessToken) {
        throw new Error("MSAL returned no accessToken");
      }

      const expMs = result.expiresOn?.getTime() ?? (Date.now() + 5 * 60_000);
      this.cachedToken = { value: result.accessToken, expMs };

      return result.accessToken;

    } catch (error: any) {
      useLogs().trackException(error as Error);
      throw error;
    }
  }

  // #region Async methods
  public async tryAcquireTokenSilent(): Promise<AuthenticationResult> {
    try {
      const instance = useMsal().instance;
      const activeAccount = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
      if (!activeAccount) {
        throw new Error("No active account found in MSAL cache");
      }

      return await instance.acquireTokenSilent({
        ...this.loginRequest,
        account: activeAccount
      });

    } catch (error) {
      useLogs().trackException(error as Error);
      throw error;
    }
  }

  public async loginByRedirect(): Promise<void> {
    try {
      const returnTo = window.location.pathname + window.location.search + window.location.hash;
      sessionStorage.setItem("returnTo", returnTo);
      await useMsal().instance.loginRedirect(this.loginRequest);
    } catch (error) {
      this.showSessionExpiryMessage();
      useLogs().trackException(error as Error);
      setTimeout(async () => await this.logout(), 1500);
    }
  }

  public async handleRedirectResult(): Promise<AuthenticationResult | null> {
    const result = await useMsal().instance.handleRedirectPromise();
    if (result?.idToken) {
      sessionStorage.setItem('idToken', result.idToken);
    }
    return result;
  }

  public async logout(): Promise<void> {
    this.user.clearUser();
    await useMsal().signout();
  }

  public setAccountOnMsalInstance(accountInfo: AccountInfo): void {
    useMsal().instance.setActiveAccount(accountInfo);
  }

  public async tryFetchAndSetUser(): Promise<boolean> {
    try {
      const response = await new Http().apiServer().get('/api/v1/me/session');
      // const user = await new User().getMe();
      this.setUser(response.data);
      return true;
    } catch (error) {
      await new NetworkError(error as AxiosError).handle();
      useLogs().trackException(error as Error);
      return false;
    }
  }
  // #endregion

  // #region Private methods
  private setUser(user: User): void {
    const userStore = useUser();
    userStore.setUser(user);

    // const parsedLocale = user.locale?.includes('en') ? 'en' : 'nl';
    // useI18n().locale.value = parsedLocale;
    // userStore.setLocale(parsedLocale);
  }

  private showSessionExpiryMessage(): void {
    useSnackbar().setSnackbar({
      title: 'Session Expired',
      message: 'Your session has expired, you must log back in.',
    });
  }
  // #endregion

  // Getters
}